import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';

import { IHttpResult, interfaces as I, interfaces } from '@leandredev/dcbm-common-lib';
import { ApiHelper } from '@leandredev/dcbm-ng-lib';

import { Observable, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import * as dcbm from '@leandredev/dcbm-ng-lib';
import { SignInJwtService } from '../signIn-jwt/signIn-jwt.service';
import { FileInfo } from 'src/app/utils/interfaces';

@Injectable({
  providedIn: 'root'
})
export class FileService {
  readonly fileServiceUrl = `${environment.data.api}fileService/`;

  private service: dcbm.service.Document;

  private types: I.IDocumentType[] = [];

  constructor(private http: HttpClient, private signInJwt: SignInJwtService) {
    this.service = new dcbm.service.Document(this.http, true);
    this.service.urlCollection = `${environment.data.api}document/`;
  }

  // API methods =======================================================================================================

  public fetchPopDocumentbyId(id: string): Observable<I.IDocument> {
    return this.service.find({}, id, true).pipe(ApiHelper.resultToObj('Request Error'));
  }

  public getDocumentById(id: string): Observable<I.IDocument> {
    return this.service.find({}, id, false).pipe(ApiHelper.resultToObj('Request Error'));
  }

  public saveDocument(document: I.IDocument): Observable<I.IDocument> {
    return this.service.create(document, {}).pipe(ApiHelper.resultToObj('Request Error'));
  }

  public updateDocument(document: I.IDocument): Observable<I.IDocument> {
    return this.service.udpate(document, {}).pipe(ApiHelper.resultToObj('Request Error'));
  }

  public fetchAllDocumentType(): Observable<I.IDocumentType[]> {
    if (this.types.length) {
      return of(this.types).pipe(first());
    } else {
      return this.service
        .types({})
        .pipe(ApiHelper.resultToArr('Request Error'))
        .pipe(map((types) => (this.types = types)));
    }
  }

  // ===================================================================================================================

  public fetchDocumentType(typeIds: string[]): Observable<I.IDocumentType[]> {
    return this.fetchAllDocumentType().pipe(
      map((types) => {
        if (typeIds.length == 0) return types;

        const filteredTypes: I.IDocumentType[] = [];

        typeIds.forEach((typeId) => {
          const foundType = types.find((type) => type._id === typeId);
          if (foundType) filteredTypes.push(foundType);
        });

        return filteredTypes;
      })
    );
  }

  public upload(file: File, progress: { value: number; mode: string }, reportProgress?: boolean): Observable<I.IFile> {
    const formData = new FormData();
    formData.append('file', file);

    progress.value = 0;
    const url = this.fileServiceUrl.concat('upload');

    return this.http
      .post(url, formData, {
        reportProgress: reportProgress,
        observe: 'events',
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            progress.value = (event.loaded / event.total) * 100;
          }
          return event;
        }),
        filter((event: HttpResponse<IHttpResult<I.IFile[]>>) => event.type === HttpEventType.Response),
        map((event) => {
          return event.body.response as I.IFile[];
        }),
        map((data: I.IFile[]) => {
          progress.value = 100;

          setTimeout(() => {
            progress.value = 0;
          }, 1000);

          return data[0];
        })
      );
  }

  public download(path: string, progress?: { value: number; mode: string }): Observable<{ blob: Blob; filename: string | undefined }> {
    progress;
    return this.http
      .get(this.fileServiceUrl.concat('file/').concat(path), {
        reportProgress: true,
        observe: 'events',
        responseType: 'blob',
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.Response && event.status === 204) {
            throw new Error('Server event ');
          }
          return event;
        }),
        filter((event: HttpResponse<Blob>) => event.type === HttpEventType.Response),
        map((response) => {
          const filenameRegex = /filename="([^"]+)"/;

          const input = response.headers.get('Content-Disposition');
          const match = input.match(filenameRegex);

          let filename: string | undefined = undefined;

          if (match) {
            filename = match[1];
          }

          return { blob: response.body, filename };
        })
      );
  }

  public uploadFile(file: File): Observable<I.IFile> {
    return of(file).pipe(
      map((file) => {
        if (!file) throw new Error('Pas de fichier sélectionne');

        return file;
      }),
      switchMap((file) => {
        const formData = new FormData();
        formData.append('file', file);

        return this.http
          .post(this.fileServiceUrl.concat('upload'), formData, {
            headers: { JWT: this.signInJwt.token() }
          })
          .pipe(
            ApiHelper.resultToArr('Erreur de téléversement du fichier'),
            map((data) => {
              return data[0];
            })
          );
      })
    );
  }

  public downloadUserClientFile(fileId: string): Observable<HttpEvent<Blob>> {
    return this.http.get(`${this.fileServiceUrl}clientFile/${fileId}`, {
      reportProgress: true,
      responseType: 'blob',
      observe: 'events',
      headers: { JWT: this.signInJwt.token() }
    });
  }

  // STORAGE
  public storageInfo(): Observable<FileInfo[]> {
    return this.http
      .get<IHttpResult<unknown>>(this.fileServiceUrl.concat('storageInfo'), {
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((response) => {
          if (response.code === 500) throw new Error(response.message);
          return response.response as FileInfo[];
        })
      );
  }

  public fileRefs(): Observable<interfaces.IFile[]> {
    return this.http
      .get<IHttpResult<unknown>>(this.fileServiceUrl.concat('fileRefs'), {
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((response) => {
          if (response.code === 500) throw new Error(response.message);
          return response.response;
        })
      );
  }

  public addEncryptionToFile(fileId: string): Observable<interfaces.IFile> {
    return this.http
      .get<IHttpResult<unknown>>(this.fileServiceUrl.concat('addEncryptionToFile/', fileId), {
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((response) => {
          if (response.code === 500) throw new Error(response.message);
          return response.response[0] as interfaces.IFile;
        })
      );
  }

  public createFileRef(file: interfaces.IFile): Observable<interfaces.IFile> {
    return this.http
      .post<IHttpResult<unknown>>(this.fileServiceUrl.concat('createFileRef'), file, {
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((response) => {
          if (response.code === 500) throw new Error(response.message);
          return response.response[0] as interfaces.IFile;
        })
      );
  }

  public removeFile(fileId: string): Observable<true> {
    return this.http
      .get<IHttpResult<unknown>>(this.fileServiceUrl.concat('removeFile/', fileId), {
        headers: { JWT: this.signInJwt.token() }
      })
      .pipe(
        map((response) => {
          if (response.code === 500) throw new Error(response.message);
          return response.response[0] as true;
        })
      );
  }
}
